import { Box, Chip } from "@mui/material";
import { startCase } from "lodash";
import queryString from "query-string";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route } from "react-router";
import { push } from "connected-react-router";
import { clearDraft } from "../../actions/bill";
import { Filters, MultipleHeader } from "../../components/List";
import useCan from "../../hooks/useCan";
import { DocumentTypes, VisitType } from "../../interfaces/BillInterfaces";
import { RootState } from "../../store";
import BillList, { Mode } from "./BillList";
import BillEditor, { LOCALSTORAGE_BILL } from "./Editor/BillEditor";
import { tl } from "../../components/translate";
import LabRequestList from "../IPD/LabRequest/LabRequestList";
import ClaimsList from "./Ssf/ClaimsList";
import ClientsWithProformaBills from "../IPD/Proforma/ClientsWithProformaBills";
import ReportsPanel from "../Reports/Panel/ReportsPanel";
import BillingPackagesList from "./BillingPackages/BillingPackagesList";
import ServiceSubTypeSelect from "../Services/ServiceSubTypeSelect";
import { SelectedServiceSubTypeProvider } from "../../hooks/useSelectedServceSubTypes";
import { useCurrentStore } from "../../hooks/useCurrentStore";
import { useCurrentCounter } from "../../hooks/useCurrentCounter";
import { BillSettingsPanel } from "../ResourceCentre/ModuleSettings/BillSettings/BillSettingsPanel";
import {
  BillCounters,
  BillStores
} from "../ResourceCentre/ModuleSettings/BillSettings/BillLocalSettings";

export enum BillModule {
  NEW = "newBill",
  NEW_CREDIT_NOTE = "newCreditNote",
  EDIT_DRAFT = "editDraft"
}

export enum IpdBilling {
  IPD_BILL = "ipdBill",
  IPD_CN = "ipdCreditNote",
  EDIT_IPD_DRAFT = "editIpdDraft"
}

const getTitle = (draft) => {
  if (!draft) return BillModule.NEW;
  if (draft.type === DocumentTypes.CREDITNOTE && draft.visitType === VisitType.OPD) {
    return BillModule.NEW_CREDIT_NOTE;
  }
  if (draft.id && draft.visitType === VisitType.OPD) {
    return BillModule.EDIT_DRAFT;
  }
  return BillModule.NEW;
};

const getIpdTitle = (draft) => {
  if (!draft) return IpdBilling.IPD_BILL;
  if (draft.type === DocumentTypes.CREDITNOTE && draft.visitType === VisitType.IPD) {
    return IpdBilling.IPD_CN;
  }
  if (draft.id && draft.visitType === VisitType.IPD) {
    return IpdBilling.EDIT_IPD_DRAFT;
  }
  return IpdBilling.IPD_BILL;
};

const getKey = (draft) => (draft?.id ? "edit" : "new");

export enum IpdTabs {
  NewBill = "newBill",
  RequestForms = "requestForms",
  ProformaBills = "proformaBills"
}

export enum ListTabs {
  Bills = "bills",
  Claims = "claims"
}

interface QueryParams {
  clientId?: string;
  bedId?: string;
  bookingId?: string;
  ipdId?: string;
  labRequestId?: string;
  productMedicationRequestId?: string;
  tab?: IpdTabs;
  isProformaBill?: string;
  purchaseRequisitionFormId?: string;
}

const Billing = ({
  match,
  location
}: {
  match: Record<string, unknown>;
  location: { pathname: string; search: string };
}): React.JSX.Element => {
  const dispatch = useDispatch();
  const { currentStore } = useCurrentStore();
  const { currentCounter } = useCurrentCounter();
  const canUseIpdBill = useCan("ipd:ipdBill", { superAdminPassReverse: true });

  // selectors
  const draft = useSelector((state: RootState) => state.bills.draft);

  // local states
  const [filter, setFilter] = React.useState(IpdTabs.NewBill);
  const [listFilter, setListFilter] = React.useState(ListTabs.Bills);

  // helper functions
  const multipleHeaders = (loc: { pathname: string }) => ({
    headers: [
      { key: getKey(draft), title: startCase(getTitle(draft)), goto: "/billing/new" },
      ...(canUseIpdBill
        ? [{ key: "ipd", title: startCase(getIpdTitle(draft)), goto: "/billing/ipd" }]
        : []),
      { key: "bills", title: tl("billing.previousBills"), goto: "/billing/bills" },
      {
        key: "commissionsAndCharges",
        title: tl("billing.spAndReferralCommissions"),
        goto: "/billing/commissionsAndCharges"
      },
      {
        key: "packages",
        title: "Billing Packages",
        goto: "/billing/packages"
      }
    ],
    url: loc.pathname
  });

  // variables
  const filterData = {
    filters: [
      { key: IpdTabs.NewBill, title: tl(`billing.newBill`) },
      { key: IpdTabs.RequestForms, title: "Request Forms" },
      { key: IpdTabs.ProformaBills, title: "Proforma Bills" }
    ]
  };

  const listFilterData = {
    filters: [
      { key: ListTabs.Bills, title: ListTabs.Bills },
      { key: ListTabs.Claims, title: ListTabs.Claims }
    ]
  };

  const {
    clientId,
    bedId,
    bookingId,
    ipdId,
    labRequestId,
    tab,
    purchaseRequisitionFormId,
    productMedicationRequestId,
    isProformaBill
  }: QueryParams = queryString.parse(location?.search);

  // effects
  React.useEffect(() => {
    if (tab) {
      setFilter(tab);
    }
  }, [tab]);

  return (
    <SelectedServiceSubTypeProvider>
      <Box height="100%">
        {/* billing header nav */}
        <Box
          sx={{
            height: "50px",
            px: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            position: "sticky",
            top: 0,
            background: "white",
            zIndex: 2
          }}
        >
          <MultipleHeader
            multipleHeaders={multipleHeaders(location)}
            onChange={() => {
              localStorage.getItem(LOCALSTORAGE_BILL);
              dispatch(clearDraft());
            }}
          />

          {draft?.type !== DocumentTypes.CREDITNOTE && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* chips for store and counter */}
              {currentStore && currentStore in BillStores && (
                <Chip
                  size="small"
                  label={BillStores[currentStore as keyof typeof BillStores]}
                  color="primary"
                />
              )}

              {currentCounter && currentCounter in BillCounters && (
                <Chip
                  size="small"
                  label={BillCounters[currentCounter as keyof typeof BillCounters]}
                  color="primary"
                />
              )}

              {/* filter by subtype */}
              <ServiceSubTypeSelect />
              <BillSettingsPanel />
              <ReportsPanel module="billing" />
            </Box>
          )}
        </Box>

        {window.location.pathname === "/billing/ipd" && (
          <Box
            sx={{
              height: "40px",
              px: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center"
            }}
          >
            <Filters
              filter={filter}
              onFilter={(key) => {
                dispatch(push(`/billing/ipd?tab=${key}`));
              }}
              filterData={filterData}
            />
          </Box>
        )}

        {(window.location.pathname.includes("bills") ||
          window.location.pathname.includes("claims")) && (
          <Box
            sx={{
              height: "40px",
              px: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center"
            }}
          >
            <Filters
              filter={listFilter}
              onFilter={(key) => {
                dispatch(push(`/billing/${key}`));
                setListFilter(key);
              }}
              filterData={listFilterData}
            />
          </Box>
        )}

        {/* routes */}
        <>
          <Route
            exact
            path={`${match.path}/new`}
            render={(props) => (
              <BillEditor
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                mode="create"
                visitType={VisitType.OPD}
                clientId={Number(clientId)}
                bookingId={Number(bookingId)}
                purchaseRequisitionFormId={Number(purchaseRequisitionFormId)}
                isProformaBill={isProformaBill === "true"}
              />
            )}
          />

          <Route
            exact
            path={`${match.path}/ipd`}
            render={(props) => {
              if (filter === IpdTabs.NewBill) {
                return (
                  <BillEditor
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                    mode="create"
                    visitType={VisitType.IPD}
                    clientId={Number(clientId)}
                    bookingId={Number(bookingId)}
                    bedId={bedId}
                    ipdId={ipdId}
                    labRequestId={labRequestId}
                    productMedicationRequestId={productMedicationRequestId}
                    isProformaBill={isProformaBill === "true"}
                  />
                );
              }

              if (filter === IpdTabs.RequestForms) {
                return (
                  <Box overflow="auto hidden" height="100%">
                    <LabRequestList />
                  </Box>
                );
              }

              return (
                <Box overflow="auto hidden" height="100%">
                  <ClientsWithProformaBills />
                </Box>
              );
            }}
          />

          <Route
            exact
            path={`${match.path}/:billId/edit`}
            render={(props) => (
              <BillEditor
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                mode="edit"
                billId={Number(props.match.params.billId)}
              />
            )}
          />

          <Route
            exact
            path={`${match.path}/ipd/:billId/edit`}
            render={(props) => (
              <BillEditor
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                mode="edit"
                billId={Number(props.match.params.billId)}
              />
            )}
          />

          <Route
            exact
            path={`${match.path}/bills/:billId?`}
            render={(props) => {
              const { billId } = props.match.params;
              return (
                (typeof billId === "undefined" || !Number.isNaN(Number(billId))) && (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <BillList
                    {...props}
                    mode={Mode.Show}
                    billId={Number(props.match.params.billId)}
                  />
                )
              );
            }}
          />

          <Route
            exact
            path={`${match.path}/commissionsAndCharges/:billId?`}
            render={(props) => {
              const { billId } = props.match.params;

              return (
                (typeof billId === "undefined" || !Number.isNaN(Number(billId))) && (
                  <BillList
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                    mode={Mode.Show}
                    billId={Number(billId)}
                    filterBillsWithCommissionsAndCharges
                  />
                )
              );
            }}
          />

          <Route exact path={`${match.path}/claims`} component={ClaimsList} />
          <Route exact path={`${match.path}/packages`} component={BillingPackagesList} />
        </>
      </Box>
    </SelectedServiceSubTypeProvider>
  );
};

export default Billing;
